<template>
    <ActivityDetailsCard
        activityType="F.L.A.G. Risk"
        :title="flag.display"
        :entity="item.entity"
        class="audit-risk-details"
    >
        <template #sub-header>
            <div class="cell wide">
                <h5>Company</h5>
                <span>{{ company.name }}</span>
            </div>
            <div class="cell wide">
                <h5>Type</h5>
                <span>{{ flag.display }}</span>
            </div>
            <div class="cell">
                <h5>Year</h5>
                <span>{{ flag.year }}</span>
            </div>
            <div class="cell">
                <h5>Severity</h5>
                <span>{{ flag.severity }}</span>
            </div>
        </template>

        <ProfileSection title="F.L.A.G. Risk">
            <template #view-all>
                <router-link
                    :to="{ name: 'executive-page', params: { id: item.entity.id }, query: { tab: 'F.L.A.G. Risk' } }"
                    >VIEW ALL &gt;</router-link
                >
            </template>
            <div class="flag-tags-container">
                <div class="flag-tags">
                    <span v-for="tag in tags" :key="tag" class="flag-tag" v-dialog.bottom="TAG_TOOLTIPS[tag]">
                        {{ tag }}
                    </span>
                </div>
                <a href="/audit-risk/legend" class="primary-link audit-risk-table-legend" target="_blank">
                    Show Legend
                </a>
            </div>

            <table class="mt-table audit-risk-table small">
                <thead>
                    <tr>
                        <th data-label="title">Category</th>
                        <th>Severity</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(flag, index) in flags" :key="index">
                        <td data-label="title">{{ flag.display }}</td>
                        <td :data-severity="flag.severity" v-dialog.left="flag.tooltip">
                            {{ flag.severity }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </ProfileSection>

        <ActivityStockPriceLine :item="item" />
    </ActivityDetailsCard>
</template>

<script setup>
import ActivityStockPriceLine from '@/components/activity/ActivityStockPriceLine'
import ActivityDetailsCard from '@/components/activity/ActivityDetailsCard'
import ProfileSection from '@/components/shared/profile-section'
import { TAG_TOOLTIPS } from '@/services/auditRiskService.js'
import { groupBy } from '@/utils/array'
import { getCompanyText } from '@/services/auditRiskService'
import { computed } from 'vue'

const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
})

const details = computed(() => props.item.details)
const flag = computed(() => details.value.flag)
const companies = computed(() => details.value.companies)
const company = computed(() => companies.value.find(c => c.id === flag.value.companyId))
const flags = computed(() => {
    let flags = [...details.value.flags]

    for (let flag of flags) {
        flag.company = companies.value.find(c => c.id === flag.companyId)
    }

    let groups = groupBy(flags, item => item.type)

    let result = []

    for (let [_, flags] of Object.entries(groups)) {
        result.push({
            display: flags[0].display,
            tooltip: flags.map(flag => `${getCompanyText(flag.company)}: ${flag.severity}`).join('\n'),
            severity: Math.max(...flags.map(flag => flag.severity)),
        })
    }

    return result.sort((a, b) => a.display.localeCompare(b.display))
})

const tags = computed(() => {
    if (!details.value.tags) {
        return []
    }

    const result = new Set(details.value.tags.map(t => t.tagType))

    return [...result].sort()
})
</script>

<style scoped>
.flag-tags-container {
    display: flex;
    flex-direction: row;
    gap: 40px;
    margin-bottom: 16px;
    width: 100%;
}

.flag-tags {
    display: flex;
    flex: 1 0 1px;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
}

.flag-tag {
    border-radius: 8px;
    color: white;
    background-color: black;
    line-height: 14px;
    font-size: 12px;
    line-height: 16px;
    padding: 4px 8px;
    text-align: left;
    white-space: nowrap;
    width: auto;
}

.audit-risk-table-legend {
    flex: 0 1 content;
    text-align: right;
    white-space: nowrap;
    width: auto;
}

table {
    clear: both;
}

[data-severity='1'] {
    background-color: #fff0a0;
}

[data-severity='2'] {
    background-color: #b6635f;
    color: white;
}

[data-severity='3'] {
    background-color: #8b0600;
    color: white;
}
</style>
